import { FileTextOutlined } from "@ant-design/icons";
import { message, notification, Table, Tooltip } from "antd";
import type {
  TablePaginationConfig,
  SorterResult,
} from "antd/es/table/interface";
import moment from "moment";
import React, { useCallback, useEffect, useState } from "react";

import CommentIcon from "../../../../svgs/comment.svg?react";
import {
  Asset,
  DepreciableCategory,
  IMessageType,
  ITransaction,
  KontaxTransactionMeta,
  UpdateTransactionState,
  ValueCategorizationType,
} from "../../../../types";
import {
  destroyMessage,
  formatAmountInCents,
  showGraphQlErrorNotification,
  showLoadingMessage,
  showMessage,
} from "../../../../utils";
import {
  onClickStopPropagation,
  shouldCellUpdate,
} from "../../../../utils/table";
import { useDeleteBusinessAssetConfirmation } from "../../../BusinessAsset/useDeleteBusinessAssetConfirmation";
import { getToBeDeletedBusinessAssets } from "../../../BusinessAsset/utils";
import Copyable from "../../../common/Copyable";
import { HoverableCommentIcon } from "../../../common/styledComponents";
import { TransactionSplitState } from "../../../common/types";
import { CategorizeBatchPopup } from "./CategorizeBatchPopup";
import { DEFAULT_PAGE_SIZE } from "./constants";
import {
  ExternalTransactionInitialValues,
  ExternalTransactionPopup,
} from "./ExternalTransactionPopup";
import {
  CheckboxCell,
  InvoiceIcon,
  MessageWrapper,
  TableWrapper,
} from "./styledComponents";
import { TransactionsTableButtons } from "./TransactionsTableButtons";
import { TransactionsTableCategory } from "./TransactionsTableCategory";
import { TransactionsTableExpandedRow } from "./TransactionsTableExpandRow";
import { TransactionsTablePopups } from "./TransactionsTablePopups";
import { TransactionsVatCategory } from "./TransactionsTableVatCategory";
import TransactionSubmissionStatus, {
  TransactionSubmissionState,
} from "./TransactionSubmissionStatus";
import { TransactionRow, UpdateSplitsState } from "./types";
import {
  findBusinessAssetsToUpdateAmount,
  getDatevSource,
  getTransactionRowClassName,
  prepareTransactionsData,
} from "./utils";
import VerificationField from "./VerificationField";
import { DISPLAY_DATE_FORMAT } from "@/v1/constants";

interface TransactionsProps {
  email: string | undefined;
  updateTransactionsMeta: (
    payload: Array<KontaxTransactionMeta>
  ) => Promise<boolean>;
  transactions: Array<ITransaction> | null;
  transactionsCount?: number;
  pagination?: {
    page: number;
    pageSize: number;
  };
  onPaginationChange?: (page: number, pageSize: number) => void;
  onSortChange?: (field: string, direction: "asc" | "desc" | null) => void;
  onFilterChange?: (field: string, value: any) => void;
  onSplitSubmit: (transactionId: string, state: TransactionSplitState) => void;
  onSplitDelete: (transactionId: string, state: TransactionSplitState) => void;
  onAssetsUpdate: (transactionId: string, assets: Array<Asset>) => void;
  updateTransactionsState: (
    state:
      | { transactions: Array<ITransaction> | null; count?: number }
      | ((prev: {
          transactions: Array<ITransaction> | null;
          count?: number;
        }) => { transactions: Array<ITransaction> | null; count?: number })
  ) => void;
  selectedTransactions: Array<ITransaction>;
  hasSelectedVerifiedTransactionsOnly?: boolean;
  verifySelectedTransactions: () => void;
  setSelection: (selectedTransactionIds: string[]) => void;
  isNested?: boolean;
}

const copyToClipboard = (text: string) => {
  navigator.clipboard.writeText(text);
  message.success("IBAN copied");
};

const TransactionsTable = ({
  email,
  updateTransactionsMeta,
  transactions,
  transactionsCount,
  pagination,
  onPaginationChange,
  onSortChange,
  onFilterChange,
  onSplitSubmit,
  onSplitDelete,
  onAssetsUpdate,
  updateTransactionsState,
  selectedTransactions,
  hasSelectedVerifiedTransactionsOnly,
  verifySelectedTransactions,
  setSelection,
  isNested,
}: TransactionsProps) => {
  const [updatingSplits, setUpdatingSplits] =
    useState<UpdateSplitsState | null>(null);

  const [showExternalTransactionPopup, setShowExternalTransactionPopup] =
    useState(false);

  const [isCategorizeBatchPopupVisible, setIsCategorizeBatchPopupVisible] =
    useState(false);

  const [expandedRowKeys, setExpandedRowKeys] = useState<string[]>([]);

  const [activeExternalTransaction, setActiveExternalTransaction] =
    useState<ITransaction>();

  const firstTransactionId = transactions?.length && transactions[0].id;
  useEffect(() => {
    setExpandedRowKeys([]);
  }, [firstTransactionId]);

  const [deleteBusinessAssetConfirmationPopup, confirmDeleteBusinessAsset] =
    useDeleteBusinessAssetConfirmation();

  const handleTablePaginationChange = useCallback(
    (
      paginationConfig: TablePaginationConfig,
      filters: any,
      sorter: SorterResult<TransactionRow> | SorterResult<TransactionRow>[]
    ) => {
      if (onPaginationChange) {
        const nextPage = paginationConfig.current ?? 1;
        const nextPageSize =
          paginationConfig.pageSize ??
          pagination?.pageSize ??
          DEFAULT_PAGE_SIZE;

        onPaginationChange(nextPage, nextPageSize);
      }

      // Handle sorting for columns
      if (onSortChange) {
        const sorterResult = Array.isArray(sorter) ? sorter[0] : sorter;
        if (
          sorterResult &&
          sorterResult.field &&
          typeof sorterResult.field === "string"
        ) {
          const direction =
            sorterResult.order === "ascend"
              ? "asc"
              : sorterResult.order === "descend"
              ? "desc"
              : null;

          // Map column field names to API field names
          const fieldMapping: Record<string, string> = {
            paymentDate: "valuta_date",
            iban: "iban",
            amount: "amount",
            description: "description",
            paidBy: "name",
            verified: "verified",
            vatCategoryCode: "vat_category_code",
            categoryCode: "category_code",
          };

          const apiFieldName =
            fieldMapping[sorterResult.field] || sorterResult.field;
          onSortChange(apiFieldName, direction);
        }
      }

      // Handle filtering for columns
      if (onFilterChange && filters) {
        // Handle assets filter (invoice column)
        if (filters.invoice !== undefined && filters.invoice !== null) {
          const filterValue = filters.invoice;
          if (Array.isArray(filterValue) && filterValue.length > 0) {
            // Filter is active: value 1 = has assets (true), value 0 = no assets (false)
            const hasAssets = filterValue[0] === 1;
            onFilterChange("assets", hasAssets);
          } else {
            // Filter is cleared
            onFilterChange("assets", undefined);
          }
        } else if (filters.invoice === null) {
          // Filter is explicitly cleared
          onFilterChange("assets", undefined);
        }

        // Handle note filter
        if (filters.note !== undefined && filters.note !== null) {
          const filterValue = filters.note;
          if (Array.isArray(filterValue) && filterValue.length > 0) {
            const selectedValues = filterValue as number[];
            // value 0 = Internal Note, value 1 = User's Note, value 2 = All
            if (selectedValues.includes(2)) {
              // "All" is selected: both true
              onFilterChange("internalNote", true);
              onFilterChange("personalNote", true);
            } else {
              // Individual filters
              const hasInternal = selectedValues.includes(0);
              const hasPersonal = selectedValues.includes(1);

              onFilterChange("internalNote", hasInternal ? true : null);
              onFilterChange("personalNote", hasPersonal ? true : null);
            }
          } else {
            // Filter is cleared
            onFilterChange("internalNote", null);
            onFilterChange("personalNote", null);
          }
        } else if (filters.note === null) {
          // Filter is explicitly cleared
          onFilterChange("internalNote", null);
          onFilterChange("personalNote", null);
        }

        // Handle verified filter
        if (filters.verified !== undefined && filters.verified !== null) {
          const filterValue = filters.verified;
          if (Array.isArray(filterValue) && filterValue.length > 0) {
            const selectedValue = filterValue[0];
            // Handle "ml" (Verified by ML) as a special case
            if (selectedValue === "ml") {
              onFilterChange("verifiedByML", true);
              onFilterChange("verified", undefined);
            } else {
              onFilterChange("verified", selectedValue as boolean);
              onFilterChange("verifiedByML", undefined);
            }
          } else {
            // Filter is cleared
            onFilterChange("verified", undefined);
            onFilterChange("verifiedByML", undefined);
          }
        } else if (filters.verified === null) {
          // Filter is explicitly cleared
          onFilterChange("verified", undefined);
          onFilterChange("verifiedByML", undefined);
        }

        // Handle submission status filter ("Submitted" column)
        if (filters.submitted !== undefined && filters.submitted !== null) {
          const filterValue = filters.submitted;
          if (Array.isArray(filterValue) && filterValue.length > 0) {
            const selectedValue = filterValue[0] as TransactionSubmissionState;
            onFilterChange("submissionStatus", selectedValue);
          } else {
            // Filter is cleared
            onFilterChange("submissionStatus", null);
          }
        } else if (filters.submitted === null) {
          // Filter is explicitly cleared
          onFilterChange("submissionStatus", null);
        }
      }
    },
    [onPaginationChange, pagination?.pageSize, onSortChange, onFilterChange]
  );

  /**
   * Update transaction meta.
   * If transaction's category has changed, associated business asset will be removed.
   * In that case, a delete confirmation modal will be displayed.
   * Updated data will be saved into the component's state, and will be processed when user clicks the confirm button.
   * Otherwise, data will be cleared from the state.
   */
  const handleUpdateTransaction = useCallback(
    async ({
      businessAssets,
      newCategoryCode,
      kontistTransactionId,
      verified,
    }: UpdateTransactionState) => {
      if (verified) {
        setExpandedRowKeys(
          expandedRowKeys.filter((key) => key !== kontistTransactionId)
        );
      }
      const loadingKey = "updating-transaction";

      try {
        showLoadingMessage(loadingKey);
        const confirmedDeleteBusinessAsset = await confirmDeleteBusinessAsset(
          getToBeDeletedBusinessAssets({
            id: kontistTransactionId,
            businessAssets,
            categoryCode: newCategoryCode,
          })
        );

        if (!confirmedDeleteBusinessAsset) {
          return;
        }

        if (verified !== undefined) {
          await updateTransactionsMeta([
            {
              kontistTransactionId,
              verified,
            },
          ]);
        } else {
          await updateTransactionsMeta([
            {
              kontistTransactionId,
              categoryCode: newCategoryCode,
              categoryCodeMeta: {
                label: undefined,
                suggestionSource: undefined,
                categorizationType: ValueCategorizationType.TAX_OPS,
              },
            },
          ]);
        }
      } catch (error) {
        showGraphQlErrorNotification("Error while updating transaction", error);
      } finally {
        destroyMessage(loadingKey);
      }
    },
    [updateTransactionsMeta, expandedRowKeys, confirmDeleteBusinessAsset]
  );

  /**
   * Update split.
   * If split's category has changed, associated business asset will be removed.
   * In that case, a delete confirmation modal will be displayed.
   * Updated data will be saved into the component's state, and will be processed when user clicks the confirm button.
   * Otherwise, data will be cleared from the state.
   */
  const handleUpdateSplits = useCallback(
    async ({
      transaction,
      splitState,
      skipConfirmationPopup = false,
    }: UpdateSplitsState) => {
      const confirmedDeleteBusinessAsset = await confirmDeleteBusinessAsset(
        getToBeDeletedBusinessAssets({
          id: transaction.id,
          businessAssets: transaction.businessAssets,
          splits: splitState.splits,
        })
      );
      if (!confirmedDeleteBusinessAsset) {
        return;
      }

      if (!skipConfirmationPopup) {
        // Split amount has changed
        const assetsToUpdateAmount = findBusinessAssetsToUpdateAmount({
          oldSplits: transaction.splits,
          newSplits: splitState.splits,
          businessAssets: transaction.businessAssets,
        });

        if (assetsToUpdateAmount?.length) {
          return setUpdatingSplits({
            transaction,
            splitState,
            assetsToUpdateAmount,
          });
        }
      }

      // Default submit behaviour
      // If the modal was shown, hide it after changing category
      setUpdatingSplits(null);

      await onSplitSubmit(transaction.id, splitState);
    },
    [onSplitSubmit, confirmDeleteBusinessAsset]
  );

  const handleDeleteSplit = useCallback(
    async (transaction: TransactionRow, state: TransactionSplitState) => {
      const confirmedDeleteBusinessAsset = await confirmDeleteBusinessAsset(
        getToBeDeletedBusinessAssets({
          id: transaction.id,
          businessAssets: transaction.businessAssets,
        })
      );
      if (!confirmedDeleteBusinessAsset) {
        return false;
      }

      onSplitDelete(transaction.id, state);
      return true;
    },
    [confirmDeleteBusinessAsset, onSplitDelete]
  );

  const onTransactionUpdated = useCallback(
    (upsertedTransaction: ITransaction) => {
      const updatedTransactions = (transactions || [])
        .filter((transaction) => transaction.id !== upsertedTransaction.id)
        .concat([upsertedTransaction])
        .sort((a: ITransaction, b: ITransaction) =>
          moment(b.valutaDate).isAfter(a.valutaDate) ? 1 : -1
        );

      updateTransactionsState((prev) => ({
        ...prev,
        transactions: updatedTransactions,
      }));

      notification.success({
        message: "Created or updated transaction successfully.",
      });
    },
    [transactions, updateTransactionsState]
  );

  const onTransactionDeleted = useCallback(
    (transactionId: string) => {
      const updatedTransactions = (transactions || [])
        .filter((transaction) => transaction.id !== transactionId)
        .sort((a: ITransaction, b: ITransaction) =>
          moment(b.valutaDate).isAfter(a.valutaDate) ? 1 : -1
        );

      updateTransactionsState((prev) => ({
        ...prev,
        transactions: updatedTransactions,
      }));

      notification.success({
        message: "Transaction deleted successfully.",
      });
    },
    [transactions, updateTransactionsState]
  );

  const columns = [
    {
      title: "Amount",
      key: "amount",
      dataIndex: "amount",
      width: 100,
      render: (amount: number, record: TransactionRow) => (
        <div data-testid="amountCell">
          {formatAmountInCents(amount, true, undefined, record.currency)}
        </div>
      ),
      sorter: true,
      align: "right" as const,
      showSorterTooltip: false,
      shouldCellUpdate,
    },
    {
      title: <FileTextOutlined className="centerIcon" />,
      key: "invoice",
      width: 60,
      render: (record: TransactionRow) => {
        return (record.assets || []).length > 0 ? (
          <InvoiceIcon data-testid="fileIcon" />
        ) : null;
      },
      align: "center" as const,
      shouldCellUpdate,
      filters: [
        {
          text: "Has Receipt",
          value: 1,
        },
        {
          text: "No Receipt",
          value: 0,
        },
      ],
      filterMultiple: false,
    },
    {
      title: () => {
        return <CommentIcon />;
      },
      key: "note",
      width: 60,
      render: (record: TransactionRow) => {
        return record.internalNote || record.personalNote ? (
          <HoverableCommentIcon />
        ) : null;
      },
      filters: [
        {
          text: "Internal Note",
          value: 0,
        },
        {
          text: "User's Note",
          value: 1,
        },
        {
          text: "All",
          value: 2,
        },
      ],
      filterMultiple: false,
      align: "center" as const,
      shouldCellUpdate,
    },
    {
      title: "Paid by",
      key: "paidBy",
      dataIndex: "paidBy",
      width: 140,
      render: (paidBy: string) => <div data-testid="paid-by">{paidBy}</div>,
      sorter: true,
      showSorterTooltip: false,
      shouldCellUpdate,
    },
    {
      title: "IBAN",
      key: "iban",
      dataIndex: "iban",
      width: 90,
      render: (iban: string | null, record: TransactionRow) => (
        <Tooltip placement="topLeft" title={iban}>
          <Copyable withFixedIcon ellipsis>
            {iban}
          </Copyable>
        </Tooltip>
      ),
      sorter: true,
      showSorterTooltip: false,
      ellipsis: {
        showTitle: false,
      },
      onCell: (record: TransactionRow) => {
        return {
          onClick: (event: any) => {
            event.stopPropagation();
            if (record.iban) copyToClipboard(record.iban);
          },
        };
      },
      shouldCellUpdate,
    },
    {
      title: "Description",
      key: "description",
      dataIndex: "description",
      width: 150,
      sorter: true,
      showSorterTooltip: false,
      shouldCellUpdate,
    },
    {
      title: "Payment Date",
      key: "paymentDate",
      dataIndex: "paymentDate",
      width: 120,
      sorter: true,
      showSorterTooltip: false,
      align: "center" as const,
      shouldCellUpdate,
      render: (paymentDate: string) => {
        return moment(paymentDate).format(DISPLAY_DATE_FORMAT);
      },
    },
    {
      title: "Verified",
      key: "verified",
      dataIndex: "verified",
      width: 130,
      render: (verified: boolean | undefined, record: TransactionRow) => {
        return (
          <div data-testid="verificationCell">
            <VerificationField
              categoryCode={record.categoryCode}
              vatCategoryCode={record.vatCategoryCode}
              categoryCodeMeta={record.categoryCodeMeta}
              vatCategoryCodeMeta={record.vatCategoryCodeMeta}
              id={record.id}
              updateTransaction={handleUpdateTransaction}
              value={verified || false}
              isSplitCategorized={record.isSplitCategorized}
              isDatevTransaction={!!getDatevSource(record)}
              verifiedBy={record.verifiedBy}
              hasAssets={!!record.assets?.length}
            />
          </div>
        );
      },
      filters: [
        {
          text: "Verified",
          value: true,
        },
        {
          text: "Not Verified",
          value: false,
        },
        {
          text: "Verified by ML",
          value: "ml",
        },
      ],
      filterMultiple: false,
      sorter: true,
      showSorterTooltip: false,
      onCell: onClickStopPropagation,
      shouldCellUpdate,
    },
    {
      title: "Category",
      key: "categoryCode",
      dataIndex: "categoryCode",
      width: 348,
      render: (
        _categoryCode: string | null | undefined,
        record: TransactionRow
      ) => {
        return (
          <TransactionsTableCategory
            record={record}
            handleUpdateTransaction={handleUpdateTransaction}
          />
        );
      },
      sorter: true,
      showSorterTooltip: false,
      onCell: onClickStopPropagation,
      shouldCellUpdate,
    },
    {
      title: "VAT Category",
      key: "vatCategoryCode",
      dataIndex: "vatCategoryCode",
      width: 348,
      render: (
        _vatCategoryCode: string | null | undefined,
        record: TransactionRow
      ) => {
        return (
          <TransactionsVatCategory
            record={record}
            updateTransactionsMeta={updateTransactionsMeta}
          />
        );
      },
      sorter: true,
      showSorterTooltip: false,
      onCell: onClickStopPropagation,
      shouldCellUpdate,
    },
    {
      title: "Submitted",
      key: "submitted",
      width: 150,
      render: (record: TransactionRow) => (
        <TransactionSubmissionStatus record={record} />
      ),
      filters: [
        {
          text: "Submitted",
          value: TransactionSubmissionState.SUBMITTED,
        },
        {
          text: "Not submitted",
          value: TransactionSubmissionState.NOT_SUBMITTED,
        },
        {
          text: "Fibu changed",
          value: TransactionSubmissionState.FIBU_CHANGED,
        },
        {
          text: "VCC changed",
          value: TransactionSubmissionState.VCC_CHANGED,
        },
        {
          text: "Fibu & VCC changed",
          value: TransactionSubmissionState.MULTIPLE_CHANGES,
        },
      ],
      filterMultiple: false,
      onCell: onClickStopPropagation,
      shouldCellUpdate,
    },
  ];

  const data = prepareTransactionsData(transactions);

  const renderExpandedRow = (transaction: TransactionRow) => {
    return (
      <TransactionsTableExpandedRow
        transaction={transaction}
        onAssetsUpdate={onAssetsUpdate}
        handleDeleteSplits={handleDeleteSplit}
        handleUpdateSplits={handleUpdateSplits}
        updateTransactionsMeta={updateTransactionsMeta}
        editExternalTransactionButtonOnClick={() => {
          setShowExternalTransactionPopup(true);
          setActiveExternalTransaction(transaction);
        }}
        onTransactionUpdated={onTransactionUpdated}
        onTransactionDeleted={onTransactionDeleted}
      />
    );
  };

  if (!transactions) {
    return (
      <MessageWrapper>
        <p>Search for transactions by email</p>
      </MessageWrapper>
    );
  }

  const onExpand = async (expanded: boolean, record: TransactionRow) => {
    const keys = expanded ? [record.key] : [];
    setExpandedRowKeys(keys);
  };

  const toggleShowCategorizeBatch = () => {
    if (
      selectedTransactions.some(
        (t) =>
          t.categoryCode &&
          Object.values<string>(DepreciableCategory).includes(t.categoryCode)
      )
    ) {
      showMessage({
        type: IMessageType.ERROR,
        content:
          "It is not possible to recategorize transactions with category 1320 or 1370",
      });
      return;
    }

    if (selectedTransactions.some((t) => t.verified)) {
      showMessage({
        type: IMessageType.ERROR,
        content: "It is not possible to recategorize verified transactions",
      });
      return;
    }
    if (selectedTransactions.some((t) => t.splits?.length)) {
      showMessage({
        type: IMessageType.ERROR,
        content: "It is not possible to batch-categorize splits",
      });
      return;
    }
    setIsCategorizeBatchPopupVisible(!isCategorizeBatchPopupVisible);
  };

  return (
    <>
      <ExternalTransactionPopup
        isPopupShown={showExternalTransactionPopup}
        onSuccess={onTransactionUpdated}
        email={email as string}
        closePopup={() => {
          setShowExternalTransactionPopup(false);
          setActiveExternalTransaction(undefined);
        }}
        initialValues={
          activeExternalTransaction as ExternalTransactionInitialValues
        }
      />

      <CategorizeBatchPopup
        isPopupVisible={isCategorizeBatchPopupVisible}
        closePopup={() => {
          toggleShowCategorizeBatch();
        }}
        selectedTransactions={selectedTransactions}
        updateTransactionsMeta={updateTransactionsMeta}
      />

      <TransactionsTableButtons
        addExternalTransactionClick={() => {
          setShowExternalTransactionPopup(true);
        }}
        transactions={transactions}
        updateTransactionsMeta={updateTransactionsMeta}
        updateTransactionsState={updateTransactionsState}
        hasSelectedTransactions={!!selectedTransactions.length}
        hasSelectedVerifiedTransactionsOnly={
          hasSelectedVerifiedTransactionsOnly
        }
        verifySelectedTransactions={verifySelectedTransactions}
        toggleShowCategorizeBatch={toggleShowCategorizeBatch}
        isNested={isNested}
      />

      <TableWrapper>
        <Table
          data-testid="verificationTable"
          rowSelection={{
            onChange: (selectedTransactionIds: React.Key[]) => {
              setSelection(selectedTransactionIds as string[]);
            },
            selectedRowKeys: selectedTransactions.map((t) => t.id),
            renderCell: (_checked, _record, _i, node) => {
              // We use this logic to be able to select the checkbox
              // also when clicking somewhere else on the cell
              // but still persist ant'shift + click logic
              return (
                <CheckboxCell
                  data-testid={`checkbox-row-${_i}`}
                  onClick={(event) => {
                    event.stopPropagation();

                    const checkbox =
                      event.currentTarget.querySelector<HTMLInputElement>(
                        'input[type="checkbox"]'
                      );

                    checkbox?.dispatchEvent(
                      new globalThis.MouseEvent(
                        event.nativeEvent.type,
                        event.nativeEvent
                      )
                    );
                  }}
                >
                  {node}
                </CheckboxCell>
              );
            },
          }}
          columns={columns}
          dataSource={data}
          bordered
          expandable={{
            expandedRowRender: renderExpandedRow,
            expandedRowClassName: () => "expandable-row",
            onExpand,
            expandedRowKeys,
            expandIconColumnIndex: -1,
          }}
          pagination={
            !isNested && {
              position: ["topRight", "bottomRight"],
              showSizeChanger: true,
              pageSizeOptions: ["50", "100", "200", "300"],
              total: transactionsCount,
              current: pagination?.page ?? 1,
              pageSize: pagination?.pageSize ?? DEFAULT_PAGE_SIZE,
            }
          }
          onChange={handleTablePaginationChange}
          scroll={{ x: "100%" }}
          rowClassName={getTransactionRowClassName}
          expandRowByClick
          size="middle"
          sticky
        />

        <TransactionsTablePopups
          handleUpdateSplits={handleUpdateSplits}
          updatingSplits={updatingSplits}
        />
        {deleteBusinessAssetConfirmationPopup}
      </TableWrapper>
    </>
  );
};

export default TransactionsTable;
